import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getComments, createComment, updateComment, deleteComment } from '../services/api';
import { MessageCircle, AlertCircle, User, Loader2, Pencil, Trash2, Check, X } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export function CommentsSection({ entityType, entityId }) {
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.user_type === 0 || user?.user_type === 5;

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', entityType, entityId],
    queryFn: () => getComments(entityType, entityId).then(r => r.data),
    enabled: !!entityId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['comments', entityType, entityId] });

  const addMutation = useMutation({
    mutationFn: (comment) => createComment({ entity_type: entityType, entity_id: entityId, comment }),
    onSuccess: () => { invalidate(); toast.success('Comment added'); },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, comment }) => updateComment(id, comment),
    onSuccess: () => { invalidate(); toast.success('Comment updated'); setEditingId(null); setEditText(''); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to update comment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteComment(id),
    onSuccess: () => { invalidate(); toast.success('Comment removed'); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to delete comment'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    addMutation.mutate(newComment);
    setNewComment('');
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.comment || c.text || '');
  };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = (id) => {
    if (!editText.trim()) { toast.error('Comment cannot be empty'); return; }
    editMutation.mutate({ id, comment: editText });
  };

  return (
    <div className="bg-white border rounded-lg p-4" data-testid={`${entityType}-comments-section`}>
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <MessageCircle size={18} />
        Comments
        <span className="text-xs text-slate-500 font-normal">(Internal Only - Not visible to clients)</span>
      </h3>

      {comments.length === 0 ? (
        <div className="text-slate-500 text-sm flex items-center gap-2 py-4">
          <AlertCircle size={16} />
          <span>There is no comment yet.</span>
        </div>
      ) : (
        <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
          {comments.map((c, idx) => {
            const canModify = isAdmin || String(c.added_by_user_id) === String(user?.id);
            const isEditing = editingId === c.id;
            return (
              <div key={c.id || `comment-${idx}`} className="bg-slate-50 p-3 rounded group" data-testid={`${entityType}-comment-${c.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <User size={14} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">{c.added_by_name || c.user_name || 'Unknown'}</span>
                  <span className="text-xs text-slate-400">{formatDateTime(c.created_at)}</span>
                  {c.updated_at && <span className="text-xs italic text-slate-400">(edited)</span>}
                  {canModify && !isEditing && (
                    <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="text-slate-400 hover:text-primary p-1"
                        title="Edit"
                        data-testid={`${entityType}-comment-edit-${c.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (window.confirm('Delete this comment?')) deleteMutation.mutate(c.id); }}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Delete"
                        data-testid={`${entityType}-comment-delete-${c.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="flex gap-2 items-start">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm"
                      data-testid={`${entityType}-comment-edit-input-${c.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => saveEdit(c.id)}
                      disabled={editMutation.isPending}
                      className="px-2 py-1 bg-primary text-white rounded text-xs flex items-center gap-1 hover:bg-primary-hover"
                      data-testid={`${entityType}-comment-edit-save-${c.id}`}
                    >
                      {editMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-2 py-1 border border-slate-300 rounded text-xs flex items-center gap-1 hover:bg-slate-50"
                      data-testid={`${entityType}-comment-edit-cancel-${c.id}`}
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.comment || c.text}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm"
          data-testid={`${entityType}-comment-input`}
        />
        <button type="submit" disabled={addMutation.isPending} className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-primary-hover flex items-center gap-1" data-testid={`${entityType}-comment-submit`}>
          {addMutation.isPending && <Loader2 size={14} className="animate-spin" />}
          Add
        </button>
      </form>
    </div>
  );
}
